import React from "react";

/**
 * Animated SVG construction site:
 * - Two tower cranes whose upper assemblies (jib + counter-jib) slew
 *   slowly around the mast top.
 * - On each crane: a trolley slides along the jib, with a hook +
 *   cargo that bobs up and down as if lifting material.
 * - Subtle drifting clouds behind.
 *
 * All movement is pure SVG/CSS — no images, no JS, fully looping.
 */
export default function CraneScene() {
  return (
    <div className="absolute inset-0 overflow-hidden bg-zinc-950">
      {/* Sky gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black" />

      {/* Drifting clouds */}
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full opacity-20"
        aria-hidden="true"
      >
        <g className="cloud-drift" fill="#52525b">
          <ellipse cx="200" cy="180" rx="160" ry="22" />
          <ellipse cx="520" cy="120" rx="220" ry="28" />
          <ellipse cx="980" cy="220" rx="180" ry="24" />
          <ellipse cx="1380" cy="150" rx="200" ry="26" />
        </g>
        <g className="cloud-drift-slow" fill="#3f3f46" opacity="0.7">
          <ellipse cx="120" cy="320" rx="240" ry="20" />
          <ellipse cx="780" cy="280" rx="260" ry="22" />
          <ellipse cx="1340" cy="340" rx="220" ry="20" />
        </g>
      </svg>

      {/* Building skyline silhouette */}
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMax slice"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <g fill="#09090b">
          {/* Tall building under construction (left) */}
          <rect x="80" y="430" width="220" height="470" />
          {/* Mid-rise (left-center) */}
          <rect x="320" y="540" width="180" height="360" />
          {/* Construction tower (center) */}
          <rect x="520" y="380" width="170" height="520" />
          {/* Industrial halls */}
          <rect x="710" y="600" width="240" height="300" />
          <rect x="960" y="560" width="120" height="340" />
          {/* Tall building (right) */}
          <rect x="1100" y="360" width="240" height="540" />
          {/* Construction frame (right) */}
          <rect x="1360" y="460" width="180" height="440" />
        </g>

        {/* Window grid hints (very subtle) */}
        <g fill="#18181b">
          {Array.from({ length: 14 }).map((_, r) =>
            Array.from({ length: 6 }).map((_, c) => (
              <rect
                key={`a-${r}-${c}`}
                x={100 + c * 32}
                y={460 + r * 30}
                width="14"
                height="16"
              />
            )),
          )}
          {Array.from({ length: 14 }).map((_, r) =>
            Array.from({ length: 6 }).map((_, c) => (
              <rect
                key={`b-${r}-${c}`}
                x={1120 + c * 36}
                y={390 + r * 32}
                width="16"
                height="18"
              />
            )),
          )}
        </g>

        {/* Random window glow */}
        <g fill="#facc15" opacity="0.5">
          <rect x="164" y="520" width="14" height="16" />
          <rect x="228" y="640" width="14" height="16" />
          <rect x="100" y="700" width="14" height="16" />
          <rect x="1156" y="486" width="16" height="18" />
          <rect x="1264" y="582" width="16" height="18" />
          <rect x="1192" y="710" width="16" height="18" />
        </g>

        {/* Construction scaffolding lines */}
        <g stroke="#27272a" strokeWidth="2" fill="none">
          <rect x="520" y="380" width="170" height="520" />
          <line x1="520" y1="440" x2="690" y2="440" />
          <line x1="520" y1="510" x2="690" y2="510" />
          <line x1="520" y1="580" x2="690" y2="580" />
          <line x1="520" y1="650" x2="690" y2="650" />
          <line x1="520" y1="720" x2="690" y2="720" />
          <line x1="520" y1="790" x2="690" y2="790" />
          <line x1="560" y1="380" x2="560" y2="900" />
          <line x1="605" y1="380" x2="605" y2="900" />
          <line x1="650" y1="380" x2="650" y2="900" />
        </g>
      </svg>

      {/* ===== CRANE 1 (left, taller) ===== */}
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMax slice"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <g stroke="#dc2626" strokeWidth="2.5" fill="none">
          {/* Mast (vertical lattice) */}
          <rect x="210" y="120" width="22" height="780" />
          <line x1="210" y1="120" x2="232" y2="900" />
          <line x1="232" y1="120" x2="210" y2="900" />
          {[200, 280, 360, 440, 520, 600, 680, 760, 840].map((y) => (
            <line key={y} x1="210" y1={y} x2="232" y2={y} />
          ))}
        </g>

        {/* Slewing upper assembly — rotates around top of mast */}
        <g className="crane-slew-1">
          {/* Operator cabin */}
          <rect x="208" y="100" width="28" height="34" fill="#facc15" stroke="#dc2626" strokeWidth="1.5" />

          {/* Counter-jib (left side) with counterweight */}
          <g stroke="#dc2626" strokeWidth="2.5" fill="none">
            <line x1="221" y1="110" x2="100" y2="110" />
            <line x1="221" y1="120" x2="100" y2="120" />
            <line x1="100" y1="110" x2="100" y2="120" />
            <line x1="221" y1="110" x2="100" y2="120" />
            <line x1="221" y1="120" x2="100" y2="110" />
          </g>
          {/* Counterweight block */}
          <rect x="90" y="120" width="36" height="28" fill="#18181b" stroke="#dc2626" strokeWidth="1.5" />

          {/* Tower tip (A-frame) */}
          <g stroke="#dc2626" strokeWidth="2" fill="none">
            <line x1="221" y1="110" x2="221" y2="40" />
            <line x1="221" y1="40" x2="160" y2="115" />
            <line x1="221" y1="40" x2="380" y2="115" />
          </g>

          {/* Long jib (right side) */}
          <g stroke="#dc2626" strokeWidth="2.5" fill="none">
            <line x1="221" y1="110" x2="650" y2="110" />
            <line x1="221" y1="120" x2="650" y2="120" />
            <line x1="650" y1="110" x2="650" y2="120" />
            {[280, 340, 400, 460, 520, 580, 620].map((x) => (
              <g key={x}>
                <line x1={x} y1="110" x2={x + 30} y2="120" />
                <line x1={x} y1="120" x2={x + 30} y2="110" />
              </g>
            ))}
          </g>

          {/* Trolley group — slides along jib */}
          <g className="crane-trolley-1">
            <rect x="-12" y="120" width="24" height="10" fill="#facc15" stroke="#dc2626" strokeWidth="1.5" />

            {/* Hook cable + load — animated bobbing */}
            <g className="crane-hook-1">
              <line x1="0" y1="130" x2="0" y2="160" stroke="#fafafa" strokeWidth="1.5" />
              <rect x="-14" y="160" width="28" height="20" fill="#27272a" stroke="#dc2626" strokeWidth="1.5" />
              {/* I-beam load */}
              <rect x="-30" y="178" width="60" height="6" fill="#52525b" />
              <rect x="-30" y="184" width="60" height="3" fill="#27272a" />
            </g>
          </g>
        </g>
      </svg>

      {/* ===== CRANE 2 (right, shorter) ===== */}
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMax slice"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <g stroke="#dc2626" strokeWidth="2.5" fill="none">
          {/* Mast */}
          <rect x="1180" y="220" width="22" height="680" />
          <line x1="1180" y1="220" x2="1202" y2="900" />
          <line x1="1202" y1="220" x2="1180" y2="900" />
          {[300, 380, 460, 540, 620, 700, 780, 860].map((y) => (
            <line key={y} x1="1180" y1={y} x2="1202" y2={y} />
          ))}
        </g>

        <g className="crane-slew-2">
          <rect x="1178" y="200" width="28" height="34" fill="#facc15" stroke="#dc2626" strokeWidth="1.5" />

          {/* Counter-jib */}
          <g stroke="#dc2626" strokeWidth="2.5" fill="none">
            <line x1="1191" y1="210" x2="1090" y2="210" />
            <line x1="1191" y1="220" x2="1090" y2="220" />
            <line x1="1090" y1="210" x2="1090" y2="220" />
            <line x1="1191" y1="210" x2="1090" y2="220" />
            <line x1="1191" y1="220" x2="1090" y2="210" />
          </g>
          <rect x="1080" y="220" width="36" height="28" fill="#18181b" stroke="#dc2626" strokeWidth="1.5" />

          {/* Tower tip A-frame */}
          <g stroke="#dc2626" strokeWidth="2" fill="none">
            <line x1="1191" y1="210" x2="1191" y2="150" />
            <line x1="1191" y1="150" x2="1140" y2="215" />
            <line x1="1191" y1="150" x2="1320" y2="215" />
          </g>

          {/* Long jib */}
          <g stroke="#dc2626" strokeWidth="2.5" fill="none">
            <line x1="1191" y1="210" x2="1500" y2="210" />
            <line x1="1191" y1="220" x2="1500" y2="220" />
            <line x1="1500" y1="210" x2="1500" y2="220" />
            {[1230, 1290, 1350, 1410, 1460].map((x) => (
              <g key={x}>
                <line x1={x} y1="210" x2={x + 30} y2="220" />
                <line x1={x} y1="220" x2={x + 30} y2="210" />
              </g>
            ))}
          </g>

          <g className="crane-trolley-2">
            <rect x="-12" y="220" width="24" height="10" fill="#facc15" stroke="#dc2626" strokeWidth="1.5" />
            <g className="crane-hook-2">
              <line x1="0" y1="230" x2="0" y2="255" stroke="#fafafa" strokeWidth="1.5" />
              <rect x="-12" y="255" width="24" height="18" fill="#27272a" stroke="#dc2626" strokeWidth="1.5" />
              <rect x="-22" y="271" width="44" height="6" fill="#52525b" />
            </g>
          </g>
        </g>
      </svg>

      {/* Bottom ground line + dust */}
      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black via-black/60 to-transparent" />
    </div>
  );
}
